import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DiagnosticReport, DiagnosticProposedAction } from "@/lib/accounting/diagnostics";
import { useAccounting } from "@/contexts/AccountingContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Wrench,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  Building2,
  PlusCircle,
  RefreshCw,
  Info
} from "lucide-react";

interface AccountingRepairDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: DiagnosticReport | null;
  onRepaired?: () => void;
}

export function AccountingRepairDialog({
  open,
  onOpenChange,
  report,
  onRepaired,
}: AccountingRepairDialogProps) {
  const { createLedger, updateLedger, refreshAll } = useAccounting();
  const { activeCompany, updateCompany } = useCompany();
  const { user } = useAuth();
  const [repairing, setRepairing] = useState(false);
  const [repairLog, setRepairLog] = useState<string[]>([]);

  if (!report) return null;

  // Flatten all proposed actions from issues
  const allActions: DiagnosticProposedAction[] = [];
  report.issues.forEach((issue) => {
    issue.proposedActions.forEach((act) => {
      // Avoid duplicate actions
      if (!allActions.some((a) => a.action === act.action && a.ledgerName === act.ledgerName)) {
        allActions.push(act);
      }
    });
  });

  const handleExecuteRepair = async () => {
    if (!report.companyId || allActions.length === 0) {
      toast.info("No actionable repairs needed.");
      onOpenChange(false);
      return;
    }

    setRepairing(true);
    const logs: string[] = [];

    try {
      let createdCount = 0;
      let updatedCount = 0;

      for (const act of allActions) {
        if (act.action === "create_ledger") {
          logs.push(`Creating ledger: ${act.ledgerName} (${act.group})...`);
          await createLedger({
            name: act.ledgerName,
            group: act.group,
            openingBalance: 0,
            openingBalanceType: act.type,
            isSystem: true,
            gstApplicable: false,
          });
          createdCount++;
        } else if (act.action === "update_company_gst") {
          logs.push(`Enabling GST registration flag on company profile...`);
          await updateCompany(report.companyId, { gstRegistered: true });
          updatedCount++;
        } else if (act.action === "update_ledger_group" && act.ledgerId) {
          logs.push(`Remapping ledger ${act.ledgerName} to ${act.group}...`);
          await updateLedger(act.ledgerId, {
            group: act.group,
            nature: act.nature,
          });
          updatedCount++;
        }
      }

      // Record audit log entry in database
      try {
        await supabase.from("audit_logs").insert({
          company_id: report.companyId,
          user_id: user?.id || "system",
          user_name: user?.name || "Administrator",
          user_role: user?.role || "company_admin",
          action: "ACCOUNTING_CONFIGURATION_REPAIR",
          module: "Accounting",
          entity_id: report.companyId,
          entity_ref: report.companyName,
          details: JSON.stringify({
            repairedIssues: report.issues.map((i) => i.code),
            actionsExecuted: allActions.map((a) => `${a.action}: ${a.ledgerName || a.reason}`),
            createdCount,
            updatedCount,
            timestamp: new Date().toISOString(),
          }),
        });
      } catch (logErr) {
        console.warn("[Repair Audit Warning]:", logErr);
      }

      // Refresh accounting context to load newly created ledgers
      if (refreshAll) {
        await refreshAll();
      }

      toast.success("Accounting configuration repaired successfully!", {
        description: `Created ${createdCount} required ledger(s) and applied ${updatedCount} configuration update(s).`,
      });

      onOpenChange(false);
      if (onRepaired) {
        onRepaired();
      }
    } catch (err: any) {
      console.error("[Accounting Repair Exception]:", err);
      toast.error("Failed to execute accounting repair", {
        description: err.message || "An unexpected error occurred during ledger creation.",
      });
    } finally {
      setRepairing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="p-2 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-lg">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">Accounting Configuration Repair</DialogTitle>
              <DialogDescription className="text-xs">
                Inspects and restores missing chart of accounts dependencies, GST ledgers, and ledger groups.
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="outline" className="text-xs font-medium gap-1 bg-muted/40">
              <Building2 className="h-3 w-3 text-muted-foreground" />
              {report.companyName}
            </Badge>
            <Badge
              variant={report.hasErrors ? "destructive" : "secondary"}
              className="text-xs"
            >
              {report.issues.length} Issue{report.issues.length === 1 ? "" : "s"} Detected
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Diagnostic Summary Alert */}
          <Alert variant={report.hasErrors ? "destructive" : "default"} className="py-2.5">
            {report.hasErrors ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <AlertTitle className="text-xs font-semibold">Diagnostic Assessment</AlertTitle>
            <AlertDescription className="text-xs">
              {report.summary}
            </AlertDescription>
          </Alert>

          {/* Issue Cards */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Diagnosed Root Causes
            </h4>
            {report.issues.map((issue) => (
              <div
                key={issue.id}
                className="border rounded-lg p-3 bg-card/60 text-xs space-y-1.5 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 font-semibold text-foreground">
                    {issue.severity === "error" ? (
                      <span className="w-2 h-2 rounded-full bg-destructive shrink-0" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                    )}
                    <span>{issue.title}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-mono shrink-0">
                    {issue.code}
                  </Badge>
                </div>
                <p className="text-muted-foreground">{issue.description}</p>
                <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-500/10 p-1.5 rounded">
                  <strong>Impact:</strong> {issue.impact}
                </div>
              </div>
            ))}
          </div>

          {/* Proposed Safe Repair Actions */}
          {allActions.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Proposed Non-Destructive Actions ({allActions.length})
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 text-[11px]">
                      <TableHead className="py-2">Action</TableHead>
                      <TableHead className="py-2">Target Ledger / Setting</TableHead>
                      <TableHead className="py-2">Accounting Group</TableHead>
                      <TableHead className="py-2 text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="text-xs divide-y">
                    {allActions.map((act, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="py-2 font-medium">
                          {act.action === "create_ledger" && (
                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                              <PlusCircle className="h-3.5 w-3.5" /> Create Ledger
                            </span>
                          )}
                          {act.action === "update_company_gst" && (
                            <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                              <RefreshCw className="h-3.5 w-3.5" /> Enable GST Flag
                            </span>
                          )}
                          {act.action === "update_ledger_group" && (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              <RefreshCw className="h-3.5 w-3.5" /> Remap Group
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 font-semibold">
                          {act.ledgerName || "Company GST Profile"}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground">
                          {act.group ? (
                            <Badge variant="secondary" className="text-[10px] font-normal">
                              {act.group} ({act.nature})
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-right font-mono text-muted-foreground">
                          ₹0.00
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Safety & Audit Guarantee Note */}
          <div className="bg-muted/30 border border-muted p-2.5 rounded-lg flex items-start gap-2.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
            <div>
              <strong className="text-foreground">Idempotent & Data-Safe:</strong> Repairs are
              transaction-safe and create missing configuration accounts with zero initial balance.
              Posted financial vouchers, historical balances, tax filings, and ledger transactions
              are strictly preserved.
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={repairing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleExecuteRepair}
            disabled={repairing || allActions.length === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
          >
            {repairing ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Executing Safe Repairs...
              </>
            ) : (
              <>
                <Wrench className="h-4 w-4" />
                Repair Now ({allActions.length} Action{allActions.length === 1 ? "" : "s"})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
